import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  Tabs,
  Tab,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';

const initialQuotes = [
  {
    id: 'QT-001',
    quoteNumber: 'QT-2026-001',
    customer: 'Acme Corporation',
    opportunity: 'Enterprise CRM License',
    amount: 25000,
    status: 'sent',
    validUntil: '2026-07-09',
    createdDate: '2026-06-09',
    items: [
      { id: 1, product: 'CRM Professional License', quantity: 10, unitPrice: 2000, total: 20000 },
      { id: 2, product: 'Implementation Support', quantity: 1, unitPrice: 5000, total: 5000 },
    ],
    discount: 0,
    tax: 0,
    notes: 'Valid for 30 days from issue date.',
    customerInfo: { name: 'Acme Corporation', email: 'contact@acme.com', phone: '+1-555-0100' },
  },
  {
    id: 'QT-002',
    quoteNumber: 'QT-2026-002',
    customer: 'TechVision Inc',
    opportunity: 'Cloud Integration Project',
    amount: 45000,
    status: 'draft',
    validUntil: '2026-07-15',
    createdDate: '2026-06-08',
    items: [
      { id: 1, product: 'Cloud Integration Module', quantity: 1, unitPrice: 30000, total: 30000 },
      { id: 2, product: 'Data Migration Service', quantity: 1, unitPrice: 15000, total: 15000 },
    ],
    discount: 0,
    tax: 0,
    notes: 'Includes 3 months of support.',
    customerInfo: { name: 'TechVision Inc', email: 'sales@techvision.com', phone: '+1-555-0101' },
  },
  {
    id: 'QT-003',
    quoteNumber: 'QT-2026-003',
    customer: 'Global Industries Ltd',
    opportunity: 'Mobile App Development',
    amount: 35000,
    status: 'accepted',
    validUntil: '2026-06-20',
    createdDate: '2026-05-25',
    items: [
      { id: 1, product: 'Mobile App Development', quantity: 1, unitPrice: 35000, total: 35000 },
    ],
    discount: 0,
    tax: 0,
    notes: 'Includes 1 year of maintenance.',
    customerInfo: { name: 'Global Industries Ltd', email: 'info@globalind.com', phone: '+1-555-0102' },
  },
  {
    id: 'QT-004',
    quoteNumber: 'QT-2026-004',
    customer: 'NextGen Startups',
    opportunity: 'SaaS Platform Licensing',
    amount: 18000,
    status: 'rejected',
    validUntil: '2026-06-15',
    createdDate: '2026-06-01',
    items: [
      { id: 1, product: 'SaaS Platform License (Annual)', quantity: 1, unitPrice: 18000, total: 18000 },
    ],
    discount: 0,
    tax: 0,
    notes: 'Client requested lower price.',
    customerInfo: { name: 'NextGen Startups', email: 'cto@nextgen.com', phone: '+1-555-0103' },
  },
  {
    id: 'QT-005',
    quoteNumber: 'QT-2026-005',
    customer: 'Enterprise Solutions Group',
    opportunity: 'Workflow Automation Suite',
    amount: 52000,
    status: 'sent',
    validUntil: '2026-07-20',
    createdDate: '2026-06-07',
    items: [
      { id: 1, product: 'Workflow Automation Suite', quantity: 1, unitPrice: 40000, total: 40000 },
      { id: 2, product: 'Training & Onboarding', quantity: 1, unitPrice: 12000, total: 12000 },
    ],
    discount: 0,
    tax: 0,
    notes: 'Pending client review.',
    customerInfo: { name: 'Enterprise Solutions Group', email: 'procurement@esg.com', phone: '+1-555-0104' },
  },
];

const statusConfig = {
  draft: { label: 'Draft', color: 'default', bgColor: '#f5f5f5' },
  sent: { label: 'Sent', color: 'info', bgColor: '#e3f2fd' },
  accepted: { label: 'Accepted', color: 'success', bgColor: '#e8f5e9' },
  rejected: { label: 'Rejected', color: 'error', bgColor: '#ffebee' },
};

function QuotationDetail({ quote, onClose, onUpdate }) {
  const theme = useTheme();
  const [drawerTab, setDrawerTab] = useState(0);
  const [editingNote, setEditingNote] = useState(quote?.notes || '');

  if (!quote) return null;

  const handleSaveNote = () => {
    onUpdate({ ...quote, notes: editingNote });
  };

  const subtotal = quote.items.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal - quote.discount + quote.tax;

  return (
    <Drawer anchor="right" open={!!quote} onClose={onClose} sx={{ '& .MuiDrawer-paper': { width: 500 } }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Quote Details</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Tabs value={drawerTab} onChange={(_, v) => setDrawerTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab label="Overview" />
        <Tab label="Items" />
        <Tab label="Pricing" />
        <Tab label="Notes" />
      </Tabs>

      <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
        {drawerTab === 0 && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Quote Number
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {quote.quoteNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Customer
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {quote.customer}
              </Typography>
              <Typography variant="caption">{quote.customerInfo.email}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Opportunity
              </Typography>
              <Typography variant="body1">{quote.opportunity}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Status
              </Typography>
              <Chip label={statusConfig[quote.status].label} color={statusConfig[quote.status].color} size="small" sx={{ mt: 0.5 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Valid Until
              </Typography>
              <Typography variant="body1">{quote.validUntil}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Created Date
              </Typography>
              <Typography variant="body1">{quote.createdDate}</Typography>
            </Box>
          </Stack>
        )}

        {drawerTab === 1 && (
          <Stack spacing={1}>
            {quote.items.map((item) => (
              <Paper key={item.id} sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f9f9f9' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {item.product}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Qty: {item.quantity}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Unit: ${item.unitPrice.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ${item.total.toLocaleString()}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {drawerTab === 2 && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Subtotal
              </Typography>
              <Typography variant="h6">${subtotal.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Discount
              </Typography>
              <Typography variant="body1">${quote.discount.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Tax
              </Typography>
              <Typography variant="body1">${quote.tax.toLocaleString()}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Amount
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                ${total.toLocaleString()}
              </Typography>
            </Box>
          </Stack>
        )}

        {drawerTab === 3 && (
          <Stack spacing={2}>
            <TextField
              label="Notes"
              multiline
              rows={6}
              fullWidth
              value={editingNote}
              onChange={(e) => setEditingNote(e.target.value)}
              variant="outlined"
            />
            <Button variant="contained" onClick={handleSaveNote}>
              Save Notes
            </Button>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function PDFPreview({ quote }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const subtotal = quote.items.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal - quote.discount + quote.tax;

  return (
    <Paper
      sx={{
        p: 4,
        backgroundColor: '#fff',
        color: '#000',
        borderRadius: 2,
        maxHeight: isMobile ? '400px' : '600px',
        overflow: 'auto',
        border: '1px solid #ddd',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          QUOTATION
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {quote.quoteNumber}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            Bill To:
          </Typography>
          <Typography variant="body2">{quote.customer}</Typography>
          <Typography variant="caption">{quote.customerInfo.email}</Typography>
          <Typography variant="caption" display="block">
            {quote.customerInfo.phone}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            Quote Details:
          </Typography>
          <Typography variant="body2">Date: {quote.createdDate}</Typography>
          <Typography variant="body2">Valid Until: {quote.validUntil}</Typography>
          <Chip label={statusConfig[quote.status].label} size="small" sx={{ mt: 0.5 }} />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Item</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Qty
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Unit Price
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {quote.items.map((item) => (
            <TableRow key={item.id}>
              <TableCell sx={{ fontSize: '0.85rem' }}>{item.product}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                {item.quantity}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                ${item.unitPrice.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                ${item.total.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ ml: 'auto', width: 250 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
          <Typography variant="body2">Subtotal:</Typography>
          <Typography variant="body2">${subtotal.toLocaleString()}</Typography>
        </Box>
        {quote.discount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2">Discount:</Typography>
            <Typography variant="body2">-${quote.discount.toLocaleString()}</Typography>
          </Box>
        )}
        {quote.tax > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2">Tax:</Typography>
            <Typography variant="body2">${quote.tax.toLocaleString()}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Total:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            ${total.toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function Quotations() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [quotes, setQuotes] = useState(initialQuotes);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuQuoteId, setMenuQuoteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newQuote, setNewQuote] = useState({
    quoteNumber: '',
    customer: '',
    opportunity: '',
    amount: '',
    validUntil: '',
    items: [{ product: '', quantity: 1, unitPrice: 0 }],
  });
  const [showPreview, setShowPreview] = useState(false);

  const pageBackground = theme.palette.mode === 'dark' ? '#121212' : '#f5f5f5';
  const pageCardBg = theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff';

  const visibleQuotes = useMemo(() => {
    return quotes.filter((q) => {
      const matchesSearch = q.quoteNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.opportunity.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [quotes, searchTerm, statusFilter]);

  const kpis = useMemo(() => {
    const draft = visibleQuotes.filter((q) => q.status === 'draft').length;
    const sent = visibleQuotes.filter((q) => q.status === 'sent').length;
    const accepted = visibleQuotes.filter((q) => q.status === 'accepted').length;
    const rejected = visibleQuotes.filter((q) => q.status === 'rejected').length;

    return [
      { label: 'Draft Quotes', count: draft, icon: '📄', color: '#9e9e9e' },
      { label: 'Sent Quotes', count: sent, icon: '📤', color: '#2196f3' },
      { label: 'Accepted Quotes', count: accepted, icon: '✓', color: '#4caf50' },
      { label: 'Rejected Quotes', count: rejected, icon: '✗', color: '#f44336' },
    ];
  }, [visibleQuotes]);

  const handleCreateQuote = () => {
    if (!newQuote.quoteNumber || !newQuote.customer) {
      setSnackbar({ open: true, message: 'Please fill in required fields', severity: 'error' });
      return;
    }

    const quote = {
      id: `QT-${Date.now()}`,
      ...newQuote,
      status: 'draft',
      createdDate: new Date().toISOString().split('T')[0],
      discount: 0,
      tax: 0,
      notes: '',
      customerInfo: { name: newQuote.customer, email: '', phone: '' },
    };

    setQuotes([...quotes, quote]);
    setOpenCreate(false);
    setNewQuote({ quoteNumber: '', customer: '', opportunity: '', amount: '', validUntil: '', items: [{ product: '', quantity: 1, unitPrice: 0 }] });
    setSnackbar({ open: true, message: 'Quote created successfully', severity: 'success' });
  };

  const handleDeleteQuote = (id) => {
    setQuotes(quotes.filter((q) => q.id !== id));
    setMenuAnchor(null);
    setSnackbar({ open: true, message: 'Quote deleted', severity: 'info' });
  };

  const handleSendQuote = (id) => {
    setQuotes(quotes.map((q) => (q.id === id ? { ...q, status: 'sent' } : q)));
    setMenuAnchor(null);
    setSnackbar({ open: true, message: 'Quote sent successfully', severity: 'success' });
  };

  const handleExportPDF = (quote) => {
    const doc = new jsPDF();
    doc.setFillColor(240, 240, 240);
    doc.rect(0, 0, 210, 297, 'F');

    doc.setFontSize(20);
    doc.text('QUOTATION', 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`Quote #: ${quote.quoteNumber}`, 20, 35);
    doc.text(`Date: ${quote.createdDate}`, 20, 45);
    doc.text(`Valid Until: ${quote.validUntil}`, 20, 55);

    doc.text('Bill To:', 20, 70);
    doc.setFontSize(9);
    doc.text(quote.customer, 20, 78);
    doc.text(quote.customerInfo.email, 20, 86);
    doc.text(quote.customerInfo.phone, 20, 94);

    doc.setFontSize(10);
    doc.setFillColor(200, 200, 200);
    doc.rect(20, 105, 170, 8, 'F');
    doc.text('Item', 25, 110);
    doc.text('Qty', 100, 110);
    doc.text('Unit Price', 130, 110);
    doc.text('Total', 165, 110);

    let yOffset = 120;
    quote.items.forEach((item) => {
      doc.setFontSize(9);
      doc.text(item.product.substring(0, 40), 25, yOffset);
      doc.text(item.quantity.toString(), 100, yOffset);
      doc.text(`$${item.unitPrice.toLocaleString()}`, 130, yOffset);
      doc.text(`$${item.total.toLocaleString()}`, 165, yOffset);
      yOffset += 8;
    });

    const subtotal = quote.items.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - quote.discount + quote.tax;

    yOffset += 5;
    doc.text(`Subtotal: $${subtotal.toLocaleString()}`, 130, yOffset);
    yOffset += 8;
    doc.text(`Total: $${total.toLocaleString()}`, 130, yOffset, { style: 'bold' });

    doc.save(`${quote.quoteNumber}.pdf`);
    setSnackbar({ open: true, message: 'PDF exported successfully', severity: 'success' });
  };

  return (
    <Box sx={{ backgroundColor: pageBackground, minHeight: '100vh', p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Quotations
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCreate(true)}>
          New Quote
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((card, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper sx={{ p: 2, borderRadius: 3, background: pageCardBg, borderLeft: `4px solid ${card.color}` }}>
              <Typography variant="caption" color="textSecondary">
                {card.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                {card.count}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>{card.icon}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Search & Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search quotes..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          sx={{ flex: isTablet ? '1 1 100%' : '1 1 300px' }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Quote Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3, backgroundColor: pageCardBg }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Quote Number</TableCell>
              <TableCell>Customer</TableCell>
              {!isMobile && <TableCell>Opportunity</TableCell>}
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              {!isMobile && <TableCell>Valid Until</TableCell>}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleQuotes.length > 0 ? (
              visibleQuotes.map((quote) => (
                <TableRow key={quote.id} sx={{ '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9' } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{quote.quoteNumber}</TableCell>
                  <TableCell>{quote.customer}</TableCell>
                  {!isMobile && <TableCell>{quote.opportunity}</TableCell>}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    ${quote.amount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip label={statusConfig[quote.status].label} color={statusConfig[quote.status].color} size="small" />
                  </TableCell>
                  {!isMobile && <TableCell>{quote.validUntil}</TableCell>}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setMenuQuoteId(quote.id);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No quotes found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setSelectedQuote(quotes.find((q) => q.id === menuQuoteId));
            setMenuAnchor(null);
            setShowPreview(false);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const quote = quotes.find((q) => q.id === menuQuoteId);
            setSelectedQuote(quote);
            setShowPreview(true);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF Preview</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExportPDF(quotes.find((q) => q.id === menuQuoteId));
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export PDF</ListItemText>
        </MenuItem>
        {quotes.find((q) => q.id === menuQuoteId)?.status === 'draft' && (
          <MenuItem onClick={() => handleSendQuote(menuQuoteId)}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send Quote</ListItemText>
          </MenuItem>
        )}
        {quotes.find((q) => q.id === menuQuoteId)?.status === 'sent' && (
          <>
            <MenuItem onClick={() => setQuotes(quotes.map((q) => (q.id === menuQuoteId ? { ...q, status: 'accepted' } : q)))}>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mark Accepted</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => setQuotes(quotes.map((q) => (q.id === menuQuoteId ? { ...q, status: 'rejected' } : q)))}>
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mark Rejected</ListItemText>
            </MenuItem>
          </>
        )}
        <MenuItem onClick={() => handleDeleteQuote(menuQuoteId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Quote Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create New Quote</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Quote Number"
              fullWidth
              value={newQuote.quoteNumber}
              onChange={(e) => setNewQuote({ ...newQuote, quoteNumber: e.target.value })}
              placeholder="QT-2026-XXX"
            />
            <TextField
              label="Customer"
              fullWidth
              value={newQuote.customer}
              onChange={(e) => setNewQuote({ ...newQuote, customer: e.target.value })}
            />
            <TextField
              label="Opportunity"
              fullWidth
              value={newQuote.opportunity}
              onChange={(e) => setNewQuote({ ...newQuote, opportunity: e.target.value })}
            />
            <TextField
              label="Amount"
              fullWidth
              type="number"
              value={newQuote.amount}
              onChange={(e) => setNewQuote({ ...newQuote, amount: parseInt(e.target.value) || 0 })}
            />
            <TextField
              label="Valid Until"
              fullWidth
              type="date"
              value={newQuote.validUntil}
              onChange={(e) => setNewQuote({ ...newQuote, validUntil: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreateQuote} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Drawer / Preview */}
      {showPreview && selectedQuote ? (
        <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            PDF Preview - {selectedQuote.quoteNumber}
            <IconButton onClick={() => setShowPreview(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <PDFPreview quote={selectedQuote} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleExportPDF(selectedQuote)} variant="contained" startIcon={<DownloadIcon />}>
              Export PDF
            </Button>
            <Button onClick={() => setShowPreview(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      ) : (
        <QuotationDetail quote={selectedQuote} onClose={() => setSelectedQuote(null)} onUpdate={(updated) => setQuotes(quotes.map((q) => (q.id === updated.id ? updated : q)))} />
      )}

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
